import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  CircleDashed,
  ExternalLink,
  Eye,
  EyeOff,
  HelpCircle,
  Key,
  Loader2,
  RefreshCw,
  RotateCcw,
  Save,
  XCircle,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { getAuthHeaders } from '../../services/adminApi';
import { cn } from '../../lib/utils';

import PageTitle from './shared/PageTitle';
import {
  PROVIDERS,
  PROVIDERS_BY_ID,
  PROVIDER_GROUPS,
  baselineFor,
  buildBaselines,
  buildUpdatePayload,
  isDraftDirty,
  isProviderId,
  isWriteOnlyField,
  validateDraft,
} from './apiKeys/providerConfig';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/** URL search param that names the one expanded provider (deep-linkable). */
export const PROVIDER_PARAM = 'provider';

// 44px minimum touch targets on every interactive control (OWZ-73).
const TOUCH = 'min-h-11';
const FIELD = 'h-11 min-h-11';

const formatTime = (date) => date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const readJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

// Backend `detail` can be a string or a FastAPI validation array; only show strings.
const detailMessage = (data, fallback) => (typeof data?.detail === 'string' && data.detail) || fallback;

const StatusPill = ({ tone, icon: Icon, children, className }) => {
  const tones = {
    good: 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-200',
    warn: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
    bad: 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200',
    info: 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
    muted: 'border-stone-200 bg-stone-50 text-stone-600 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-300',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium',
        tones[tone],
        className
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
      {children}
    </span>
  );
};

const testSummary = (testing, result) => {
  if (testing) return { tone: 'info', icon: Loader2, label: 'Testing…' };
  if (!result) return { tone: 'muted', icon: CircleDashed, label: 'Not tested' };
  return result.success
    ? { tone: 'good', icon: CheckCircle2, label: `Test passed ${formatTime(result.at)}` }
    : { tone: 'bad', icon: XCircle, label: `Test failed ${formatTime(result.at)}` };
};

const SetupGuide = ({ provider }) => {
  const [open, setOpen] = useState(false);
  const contentId = `api-keys-${provider.id}-setup`;
  return (
    <div className="rounded-lg border border-stone-200 dark:border-stone-700">
      <button
        type="button"
        className={cn(
          TOUCH,
          'flex w-full items-center justify-between gap-2 rounded-lg px-3 text-left text-sm font-medium text-stone-700 hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:text-stone-200 dark:hover:bg-stone-800'
        )}
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-amber-600" aria-hidden="true" />
          Setup guide
        </span>
        <ChevronDown className={cn('h-4 w-4 transition-transform motion-reduce:transition-none', open && 'rotate-180')} aria-hidden="true" />
      </button>
      {open && (
        <div id={contentId} className="space-y-3 border-t border-stone-200 px-3 py-3 dark:border-stone-700">
          <ol className="list-decimal space-y-1.5 pl-5 text-sm text-stone-700 dark:text-stone-300">
            {provider.setup.steps.map((step) => (
              <li key={step} className="break-words">{step}</li>
            ))}
          </ol>
          <a
            href={provider.setup.link}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              TOUCH,
              'inline-flex items-center gap-2 text-sm font-medium text-amber-700 underline-offset-4 hover:underline dark:text-amber-400'
            )}
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            {provider.setup.linkText}
          </a>
        </div>
      )}
    </div>
  );
};

const SecretToggle = ({ revealed, label, controls, onToggle, className }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={`${revealed ? 'Hide' : 'Show'} ${label.toLowerCase()}`}
    aria-pressed={revealed}
    aria-controls={controls}
    className={cn(
      'absolute right-0 flex h-11 w-11 items-center justify-center rounded-md text-stone-500 hover:text-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:text-stone-400 dark:hover:text-stone-100',
      className
    )}
  >
    {revealed ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
  </button>
);

const ProviderField = ({ field, value, error, revealed, onToggleReveal, onChange }) => {
  const id = `api-keys-${field.key}`;
  const errorId = `${id}-error`;
  const helpId = `${id}-help`;
  const describedBy = [field.help && helpId, error && errorId].filter(Boolean).join(' ') || undefined;
  const invalid = Boolean(error);
  const writeOnly = isWriteOnlyField(field);
  const writeOnlyRef = useRef(null);

  // Write-only inputs are uncontrolled: React would otherwise mirror a
  // controlled value into the `value` attribute / textarea text, putting the
  // secret into the serialised DOM. Sync the live property from state instead.
  useEffect(() => {
    const node = writeOnlyRef.current;
    if (writeOnly && node && node.value !== value) node.value = value;
  }, [value, writeOnly]);

  const secretInputProps = {
    ref: writeOnlyRef,
    autoComplete: 'off',
    autoCapitalize: 'off',
    autoCorrect: 'off',
    spellCheck: false,
    'data-secret-input': 'true',
  };

  let control;
  if (field.kind === 'secret' || field.kind === 'credential') {
    const masked = field.kind === 'secret' && !revealed;
    control = (
      <div className="relative">
        <Input
          id={id}
          type={masked ? 'password' : 'text'}
          placeholder={field.placeholder}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          className={cn(FIELD, field.kind === 'secret' && 'pr-12')}
          {...secretInputProps}
        />
        {field.kind === 'secret' && (
          <SecretToggle
            revealed={revealed}
            label={field.label}
            controls={id}
            onToggle={onToggleReveal}
            className="top-0"
          />
        )}
      </div>
    );
  } else if (field.kind === 'secret-json') {
    control = (
      <div className="relative">
        <textarea
          id={id}
          className="min-h-[120px] w-full resize-y rounded-md border border-input bg-background p-2 pr-12 font-mono text-xs text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          placeholder={field.placeholder}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          // Masks the pasted JSON until the admin explicitly reveals it.
          style={{ WebkitTextSecurity: revealed ? 'none' : 'disc' }}
          {...secretInputProps}
        />
        <SecretToggle
          revealed={revealed}
          label="credentials"
          controls={id}
          onToggle={onToggleReveal}
          className="top-0"
        />
      </div>
    );
  } else if (field.kind === 'select') {
    control = (
      <select
        id={id}
        className={cn(FIELD, 'w-full rounded-md border border-input bg-background px-2 text-sm text-foreground')}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={describedBy}
      >
        {field.options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    );
  } else if (field.kind === 'range') {
    control = (
      <>
        <input
          id={id}
          type="range"
          min={field.min}
          max={field.max}
          step={field.step}
          value={value}
          onChange={(event) => onChange(parseFloat(event.target.value))}
          aria-valuetext={`${value}x`}
          className={cn(FIELD, 'w-full accent-amber-600')}
        />
        <div className="flex justify-between text-xs text-stone-500 dark:text-stone-400" aria-hidden="true">
          <span>{field.min}x</span>
          <span>1.0x</span>
          <span>{field.max}x</span>
        </div>
      </>
    );
  } else {
    control = (
      <Input
        id={id}
        placeholder={field.placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={describedBy}
        className={FIELD}
      />
    );
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{field.label}</Label>
        {field.kind === 'range' && (
          <span className="text-sm text-stone-500 dark:text-stone-400">{value}x</span>
        )}
      </div>
      {control}
      {field.help && (
        <p id={helpId} className="text-xs text-stone-500 dark:text-stone-400">{field.help}</p>
      )}
      {error && (
        <p id={errorId} className="flex items-center gap-1 text-sm text-red-700 dark:text-red-300">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
};

const ProviderPanel = ({
  provider,
  settings,
  draft,
  dirty,
  errors,
  saving,
  saveLocked,
  saveStatus,
  testing,
  testResult,
  revealed,
  onFieldChange,
  onToggleReveal,
  onSave,
  onDiscard,
  onTest,
}) => {
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const formRef = useRef(null);
  const discardTriggerRef = useRef(null);
  const keepEditingRef = useRef(null);
  const focusAfterConfirmRef = useRef(null);
  const configured = Boolean(settings?.[provider.configuredKey]);
  const identifier = provider.identifier(settings);
  // One save at a time across providers (PUT responses replace the whole
  // settings snapshot), and never a save and a test of the same provider at
  // once (a stale test result could otherwise describe replaced credentials).
  const canSave = dirty && !saving && !saveLocked && !testing;

  useEffect(() => {
    if (!dirty) setConfirmingDiscard(false);
  }, [dirty]);

  // Keyboard focus enters the confirmation and returns to a sensible control.
  useEffect(() => {
    if (confirmingDiscard) {
      keepEditingRef.current?.focus();
      return;
    }
    const target = focusAfterConfirmRef.current;
    focusAfterConfirmRef.current = null;
    if (target === 'trigger') discardTriggerRef.current?.focus();
    if (target === 'field') formRef.current?.querySelector('input, textarea, select')?.focus();
  }, [confirmingDiscard]);

  const closeConfirm = (focusTarget) => {
    focusAfterConfirmRef.current = focusTarget;
    setConfirmingDiscard(false);
  };

  return (
    <div className="space-y-4 border-t border-stone-200 px-3 pb-4 pt-4 dark:border-stone-700 sm:px-4 [&>*]:max-w-3xl">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {configured ? (
          <StatusPill tone="good" icon={CheckCircle2}>Configured</StatusPill>
        ) : (
          <StatusPill tone="warn" icon={XCircle}>Not configured</StatusPill>
        )}
        {identifier && (
          <span className="break-all font-mono text-xs text-stone-500 dark:text-stone-400">{identifier}</span>
        )}
      </div>

      <SetupGuide provider={provider} />

      <form
        ref={formRef}
        className="space-y-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (canSave) onSave();
        }}
      >
        <fieldset className="space-y-3" disabled={saving}>
          <legend className="sr-only">{provider.name} settings</legend>
          {provider.fields.map((field) => (
            <ProviderField
              key={field.key}
              field={field}
              value={draft[field.key]}
              error={errors?.[field.key]}
              revealed={revealed}
              onToggleReveal={onToggleReveal}
              onChange={(value) => onFieldChange(field.key, value)}
            />
          ))}
        </fieldset>

        {errors?._form && (
          <p className="flex items-center gap-1 text-sm text-red-700 dark:text-red-300">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {errors._form}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" className={TOUCH} disabled={!canSave}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Save className="h-4 w-4" aria-hidden="true" />}
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className={TOUCH}
            onClick={onTest}
            disabled={testing || saving || !configured}
          >
            {testing ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="h-4 w-4" aria-hidden="true" />}
            {testing ? 'Testing…' : 'Test connection'}
          </Button>
          {dirty && !confirmingDiscard && (
            <Button
              ref={discardTriggerRef}
              type="button"
              variant="ghost"
              className={TOUCH}
              onClick={() => setConfirmingDiscard(true)}
              disabled={saving}
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
              Discard changes
            </Button>
          )}
        </div>

        {confirmingDiscard && (
          <div
            role="alertdialog"
            aria-labelledby={`api-keys-${provider.id}-discard`}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                closeConfirm('trigger');
              }
            }}
            className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/30 sm:flex-row sm:items-center sm:justify-between"
          >
            <p id={`api-keys-${provider.id}-discard`} className="text-sm text-amber-900 dark:text-amber-100">
              Discard unsaved changes to {provider.shortName}?
            </p>
            <div className="flex gap-2">
              <Button
                ref={keepEditingRef}
                type="button"
                variant="outline"
                className={TOUCH}
                onClick={() => closeConfirm('trigger')}
              >
                Keep editing
              </Button>
              <Button
                type="button"
                variant="destructive"
                className={TOUCH}
                onClick={() => {
                  closeConfirm('field');
                  onDiscard();
                }}
              >
                Discard
              </Button>
            </div>
          </div>
        )}

        {dirty && configured && (
          <p className="text-xs text-stone-500 dark:text-stone-400">
            Test connection checks the saved credentials. Save first to test new values.
          </p>
        )}
      </form>

      <div aria-live="polite" className="space-y-2">
        {saveStatus && (
          <div
            data-status={`save-${saveStatus.type}`}
            className={cn(
              'flex items-start gap-2 rounded-md p-2 text-sm',
              saveStatus.type === 'success'
                ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200'
            )}
          >
            {saveStatus.type === 'success'
              ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              : <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}
            <span>{saveStatus.message}</span>
          </div>
        )}
        {testResult && (
          <div
            data-status={testResult.success ? 'test-success' : 'test-failure'}
            className={cn(
              'flex items-start gap-2 break-words rounded-md p-2 text-sm',
              testResult.success
                ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200'
            )}
          >
            {testResult.success
              ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              : <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}
            <span>{testResult.message}</span>
          </div>
        )}
      </div>

      {provider.note && (
        <p className="text-xs text-stone-500 dark:text-stone-400">{provider.note}</p>
      )}
    </div>
  );
};

const APIKeysSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Per-provider state. Drafts survive collapsing a provider so switching
  // providers (or Back/Forward) never silently discards edits.
  const [drafts, setDrafts] = useState(() => buildBaselines(null));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState({});
  const [saveStatus, setSaveStatus] = useState({});
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});
  const [revealed, setRevealed] = useState({});

  const rowRefs = useRef({});
  const scrolledForDeepLink = useRef(false);

  const requestedProvider = searchParams.get(PROVIDER_PARAM);
  const expandedId = isProviderId(requestedProvider) ? requestedProvider : null;

  const baselines = useMemo(() => buildBaselines(settings), [settings]);
  const dirtyIds = useMemo(
    () => PROVIDERS.filter((provider) => isDraftDirty(provider, drafts[provider.id], baselines[provider.id]))
      .map((provider) => provider.id),
    [baselines, drafts]
  );
  const isDirty = (id) => dirtyIds.includes(id);
  const anySaving = Object.values(saving).some(Boolean);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/admin/api-keys`, { headers: getAuthHeaders() });
        if (cancelled) return;
        if (response.ok) {
          const data = await response.json();
          if (cancelled) return;
          setSettings(data);
          setDrafts(buildBaselines(data));
        } else {
          setLoadError('Failed to load settings');
        }
      } catch {
        if (!cancelled) setLoadError('Network error loading settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Reloading or closing the tab with unsaved provider edits asks first.
  useEffect(() => {
    if (dirtyIds.length === 0) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirtyIds.length]);

  // Collapsing a provider re-hides any revealed secret.
  useEffect(() => {
    setRevealed((current) => (
      Object.keys(current).some((id) => id !== expandedId && current[id])
        ? Object.fromEntries(Object.entries(current).filter(([id]) => id === expandedId))
        : current
    ));
  }, [expandedId]);

  // Deep link: bring the requested provider into view once the list renders.
  useEffect(() => {
    if (loading || !expandedId || scrolledForDeepLink.current) return;
    scrolledForDeepLink.current = true;
    rowRefs.current[expandedId]?.scrollIntoView?.({ block: 'start' });
  }, [expandedId, loading]);

  const setExpanded = useCallback((id) => {
    scrolledForDeepLink.current = true;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (id) next.set(PROVIDER_PARAM, id);
      else next.delete(PROVIDER_PARAM);
      return next;
    });
  }, [setSearchParams]);

  const toggleProvider = (id) => {
    if (expandedId === id) {
      setExpanded(null);
      return;
    }
    setExpanded(id);
    requestAnimationFrame(() => rowRefs.current[id]?.scrollIntoView?.({ block: 'nearest' }));
  };

  const handleFieldChange = (providerId, key, value) => {
    setDrafts((current) => ({ ...current, [providerId]: { ...current[providerId], [key]: value } }));
    setErrors((current) => {
      const providerErrors = current[providerId];
      if (!providerErrors?.[key] && !providerErrors?._form) return current;
      const { [key]: _removed, _form: _formRemoved, ...rest } = providerErrors;
      return { ...current, [providerId]: rest };
    });
    setSaveStatus((current) => (current[providerId] ? { ...current, [providerId]: null } : current));
  };

  const discardProvider = (providerId) => {
    setDrafts((current) => ({ ...current, [providerId]: baselines[providerId] }));
    setErrors((current) => ({ ...current, [providerId]: {} }));
    setRevealed((current) => ({ ...current, [providerId]: false }));
    setSaveStatus((current) => ({ ...current, [providerId]: null }));
  };

  const saveProvider = async (providerId) => {
    if (anySaving || testing[providerId]) return;
    const provider = PROVIDERS_BY_ID[providerId];
    const draft = drafts[providerId];
    const validation = validateDraft(provider, draft);
    if (Object.keys(validation).length > 0) {
      setErrors((current) => ({ ...current, [providerId]: validation }));
      setSaveStatus((current) => ({
        ...current,
        [providerId]: { type: 'error', message: 'Fix the highlighted fields, then save again.' },
      }));
      return;
    }

    setErrors((current) => ({ ...current, [providerId]: {} }));
    setSaveStatus((current) => ({ ...current, [providerId]: null }));
    setSaving((current) => ({ ...current, [providerId]: true }));

    try {
      const response = await fetch(`${API_BASE}/api/admin/api-keys`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(buildUpdatePayload(provider, draft)),
      });
      const data = await readJson(response);
      if (response.ok) {
        setSettings(data);
        // Saved: write-only fields clear (the response only carries masks).
        setDrafts((current) => ({ ...current, [providerId]: baselineFor(provider, data) }));
        setRevealed((current) => ({ ...current, [providerId]: false }));
        // An earlier test result described the previous credentials.
        setTestResults((current) => ({ ...current, [providerId]: null }));
        setSaveStatus((current) => ({
          ...current,
          [providerId]: { type: 'success', message: `${provider.shortName} settings saved at ${formatTime(new Date())}.` },
        }));
      } else {
        setSaveStatus((current) => ({
          ...current,
          [providerId]: { type: 'error', message: detailMessage(data, 'Failed to save settings') },
        }));
      }
    } catch {
      setSaveStatus((current) => ({
        ...current,
        [providerId]: { type: 'error', message: 'Network error saving settings' },
      }));
    } finally {
      setSaving((current) => ({ ...current, [providerId]: false }));
    }
  };

  const testProvider = async (providerId) => {
    if (testing[providerId] || saving[providerId]) return;
    const provider = PROVIDERS_BY_ID[providerId];
    setTesting((current) => ({ ...current, [providerId]: true }));
    setTestResults((current) => ({ ...current, [providerId]: null }));
    let result;
    try {
      const response = await fetch(`${API_BASE}/api/admin/api-keys/test/${provider.testSlug}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await readJson(response);
      result = response.ok
        ? { success: true, message: data.message || 'Connection succeeded' }
        : { success: false, message: detailMessage(data, 'Connection test failed') };
    } catch {
      result = { success: false, message: 'Network error' };
    }
    setTestResults((current) => ({ ...current, [providerId]: { ...result, at: new Date() } }));
    setTesting((current) => ({ ...current, [providerId]: false }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" aria-label="Loading API keys" />
      </div>
    );
  }

  const configuredCount = PROVIDERS.filter((provider) => settings?.[provider.configuredKey]).length;

  return (
    <div className="space-y-4" data-testid="api-keys-settings">
      <PageTitle
        title="API Keys"
        description="Manage external service API keys and configurations"
        icon={<Key className="h-6 w-6" />}
        className="mb-2"
      />

      {loadError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          {loadError}
        </div>
      )}

      <p className="text-sm text-stone-600 dark:text-stone-400" data-testid="api-keys-summary">
        {configuredCount} of {PROVIDERS.length} providers configured
        {dirtyIds.length > 0 && (
          <span className="font-medium text-amber-700 dark:text-amber-400">
            {' · '}
            {dirtyIds.length} with unsaved changes
          </span>
        )}
      </p>

      {PROVIDER_GROUPS.map((group) => (
        <section key={group.id} aria-labelledby={`api-keys-group-${group.id}`} className="space-y-2">
          <h2
            id={`api-keys-group-${group.id}`}
            className="text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400"
          >
            {group.label}
          </h2>
          <ul className="space-y-2">
            {group.providerIds.map((providerId) => {
              const provider = PROVIDERS_BY_ID[providerId];
              const Icon = provider.icon;
              const expanded = expandedId === providerId;
              const configured = Boolean(settings?.[provider.configuredKey]);
              const test = testSummary(testing[providerId], testResults[providerId]);
              const buttonId = `api-keys-${providerId}-toggle`;
              const panelId = `api-keys-${providerId}-panel`;
              return (
                <li
                  key={providerId}
                  ref={(node) => { rowRefs.current[providerId] = node; }}
                  data-provider-row={providerId}
                  data-expanded={expanded ? 'true' : 'false'}
                  className={cn(
                    'scroll-mt-4 rounded-lg border bg-card text-card-foreground shadow-sm',
                    expanded ? 'border-amber-400 dark:border-amber-600' : 'border-stone-200 dark:border-stone-700'
                  )}
                >
                  <h3 className="m-0">
                    <button
                      type="button"
                      id={buttonId}
                      aria-expanded={expanded}
                      aria-controls={panelId}
                      onClick={() => toggleProvider(providerId)}
                      className="flex min-h-14 w-full items-center gap-3 rounded-lg px-3 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 sm:px-4"
                    >
                      <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', provider.accent)}>
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-stone-900 dark:text-stone-100">
                          {provider.name}
                        </span>
                        <span className="mt-1 flex flex-wrap gap-1.5">
                          {configured ? (
                            <StatusPill tone="good" icon={CheckCircle2}>Configured</StatusPill>
                          ) : (
                            <StatusPill tone="warn" icon={XCircle}>Not configured</StatusPill>
                          )}
                          <StatusPill tone={test.tone} icon={test.icon}>{test.label}</StatusPill>
                          {isDirty(providerId) && (
                            <StatusPill tone="warn">Unsaved changes</StatusPill>
                          )}
                        </span>
                      </span>
                      <ChevronDown
                        className={cn('h-5 w-5 shrink-0 text-stone-500 transition-transform motion-reduce:transition-none', expanded && 'rotate-180')}
                        aria-hidden="true"
                      />
                    </button>
                  </h3>
                  <div id={panelId} role="region" aria-labelledby={buttonId} hidden={!expanded}>
                    {expanded && (
                      <ProviderPanel
                        provider={provider}
                        settings={settings}
                        draft={drafts[providerId]}
                        dirty={isDirty(providerId)}
                        errors={errors[providerId]}
                        saving={Boolean(saving[providerId])}
                        saveLocked={anySaving}
                        saveStatus={saveStatus[providerId]}
                        testing={Boolean(testing[providerId])}
                        testResult={testResults[providerId]}
                        revealed={Boolean(revealed[providerId])}
                        onFieldChange={(key, value) => handleFieldChange(providerId, key, value)}
                        onToggleReveal={() => setRevealed((current) => ({ ...current, [providerId]: !current[providerId] }))}
                        onSave={() => saveProvider(providerId)}
                        onDiscard={() => discardProvider(providerId)}
                        onTest={() => testProvider(providerId)}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {settings?.updated_at && (
        <p className="text-center text-sm text-stone-500 dark:text-stone-400">
          Last updated: {new Date(settings.updated_at).toLocaleString()}
          {settings.updated_by && ` by ${settings.updated_by}`}
        </p>
      )}

    </div>
  );
};

export default APIKeysSettings;
